import React, { useMemo, useState } from 'react'
import IconButton from '@material-ui/core/IconButton'
import ListAltIcon from '@material-ui/icons/ListAlt'
import SpeedOutlinedIcon from '@material-ui/icons/SpeedOutlined'
import { splitFleetUnits } from '../fleetLiveHelpers'
import { getDownholeFlowReading } from './pumpCardHelpers'
import PumpTiles from './PumpTiles'
import PumpStatusGrid from './PumpStatusGrid'
import PumpManifold from './PumpManifold'
import PumpTable, {
  READINGS_LAYOUT_CHANNELS_TOP,
  READINGS_LAYOUT_UNITS_TOP,
} from './PumpTable'
import PumpDetailPanel from './PumpDetailPanel'
import BlenderTiles, { getBlenderPrimaryReading } from './BlenderTiles'
import FleetChartPanel from './FleetChartPanel'
import { collectStandardChannelOptions } from './fleetChartHelpers'

const VIEW_TILES = 'tiles'
const VIEW_GRID = 'grid'
const VIEW_MANIFOLD = 'manifold'
const VIEW_READINGS = 'readings'

const VIEW_OPTIONS = [
  [VIEW_TILES, 'Tiles', 'Detailed tiles'],
  [VIEW_GRID, 'Grid', 'Status grid'],
  [VIEW_MANIFOLD, 'Manifold', 'Manifold / Missile'],
  [VIEW_READINGS, 'Readings', 'All readings'],
]

// Pumps from all job types are not one MPC fleet, so there is no status grid or manifold layout for them
const ALL_SOURCES_VIEW_OPTIONS = VIEW_OPTIONS.filter(([id]) => id === VIEW_TILES || id === VIEW_READINGS)

const FracFleetView = ({
  units = [],
  hasReceivedData = false,
  hasPumpJob = true,
  allSources = false,
  selectedUnitUId,
  detailUnit,
  onSelectUnit,
  onCloseDetail,
  fleetType,
  licenseIds,
  chartRequest,
  chartSeriesByUnit,
  chartDateOriginMs,
  chartChannelUnit,
  chartFieldName,
  onOpenFleetChart,
  onCloseFleetChart,
  onChartChannelChange,
  onChartDateTypeChange,
  blenderHistory,
  jobPairs,
  pendingFieldChannels,
  onSetFieldChannel,
  onResetFieldChannel,
  translation,
}) => {
  const { pumps, blenders } = useMemo(() => splitFleetUnits(units), [units])
  const treating = useMemo(() => (blenders[0] ? getBlenderPrimaryReading(blenders[0]) : null), [blenders])
  const downholeFlow = useMemo(() => getDownholeFlowReading(pumps), [pumps])
  const [selectedView, setView] = useState(VIEW_TILES)
  const viewOptions = allSources ? ALL_SOURCES_VIEW_OPTIONS : VIEW_OPTIONS
  const view = viewOptions.some(([id]) => id === selectedView) ? selectedView : VIEW_TILES
  const [readingsLayout, setReadingsLayout] = useState(READINGS_LAYOUT_UNITS_TOP)

  const selectedPump = useMemo(
    () => {
      if (!selectedUnitUId) return null
      if (detailUnit && detailUnit.unitUId === selectedUnitUId) return detailUnit
      return pumps.find((pump) => pump.unitUId === selectedUnitUId) || null
    },
    [pumps, selectedUnitUId, detailUnit]
  )

  const isDetailLoading = Boolean(
    selectedUnitUId
    && (!detailUnit || detailUnit.unitUId !== selectedUnitUId)
  )

  const channelOptions = useMemo(() => collectStandardChannelOptions(pumps), [pumps])

  const activeViewIndex = viewOptions.findIndex(([id]) => id === view)
  const switchClassName = viewOptions.length === 2
    ? `optionsSwitch${activeViewIndex > 0 ? ' in' : ''}`
    : `optionsSwitch optionsSwitch--4${activeViewIndex > 0 ? ` in-${activeViewIndex}` : ''}`

  const handleViewChange = (nextView) => {
    setView(nextView)
    if (nextView !== VIEW_READINGS && chartRequest) {
      onCloseFleetChart?.()
    }
    if (nextView === VIEW_READINGS && selectedUnitUId) {
      onCloseDetail?.()
    }
  }

  const toggleReadingsLayout = () => {
    setReadingsLayout((prev) => (
      prev === READINGS_LAYOUT_UNITS_TOP
        ? READINGS_LAYOUT_CHANNELS_TOP
        : READINGS_LAYOUT_UNITS_TOP
    ))
  }

  const layoutToggleTitle = readingsLayout === READINGS_LAYOUT_UNITS_TOP
    ? (translation?.fleetReadingsLayoutChannelsTopLabel || 'Show channels as columns')
    : (translation?.fleetReadingsLayoutUnitsTopLabel || 'Show units as columns')

  // Pumps come from MPC Units jobs only (any job with all sources): no such job, or one without pumps, means nothing to show for them
  const hasNoMpcPumps = !hasPumpJob || (hasReceivedData && !pumps.length)

  if (!units.length && hasPumpJob) {
    return (
      <div className="pageBlock">
        <div className="pageCard">
          <div className="fleetEmptyState">
            {translation?.fleetWaitingForDataLabel || 'Waiting for data…'}
            {hasReceivedData ? (
              <div className="fleetEmptyHint">
                {translation?.fleetNoChannelMappingsHint
                  || 'No channel mappings initialized'}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <BlenderTiles
        blenders={blenders}
        history={blenderHistory}
        jobPairs={jobPairs}
        pendingFieldChannels={pendingFieldChannels}
        onSetFieldChannel={onSetFieldChannel}
        onResetFieldChannel={onResetFieldChannel}
        translation={translation}
      />

      {hasNoMpcPumps ? (
        <div className="pageBlock">
          <div className="pageCard">
            <div className="fdNoMpcData">
              <div className="fdNoMpcDataIcon">
                <SpeedOutlinedIcon fontSize="inherit" />
              </div>
              <div className="fdNoMpcDataTitle">
                {allSources
                  ? (translation?.fleetNoPumpDataTitle || 'No pump data for this fleet')
                  : (translation?.fleetNoMpcDataTitle || 'No MPC data for this fleet')}
              </div>
              <div className="fdNoMpcDataHint">
                {allSources
                  ? (translation?.fleetNoPumpDataHint
                    || 'None of the fleet licenses has a job with pumps right now.')
                  : (translation?.fleetNoMpcDataHint
                    || 'Pumps are shown from MPC Units jobs. None of the fleet licenses has an MPC Units job with pumps right now.')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="pageBlock">
          <div className="pageCard">
            <div className="fdPumpsHead">
              <div className="fdPumpsTitle">
                <span className="title">{translation?.fleetPumpsLabel || 'Pumps'}</span>
                <span className="meta">
                  {pumps.length} {translation?.fleetUnitsLabel || 'units'}
                </span>
                {view === VIEW_READINGS ? (
                  <IconButton
                    className="fdPumpsLayoutToggle"
                    size="small"
                    onClick={toggleReadingsLayout}
                    title={layoutToggleTitle}
                    aria-label={layoutToggleTitle}
                  >
                    <ListAltIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </div>
              <div className={switchClassName}>
                {viewOptions.map(([id, label, fullLabel]) => (
                  <div
                    key={id}
                    className="option"
                    title={fullLabel || label}
                    onClick={() => handleViewChange(id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleViewChange(id)
                    }}
                  >
                    <span className="fdPumpsViewLabel fdPumpsViewLabel--short">{label}</span>
                    <span className="fdPumpsViewLabel fdPumpsViewLabel--full">{fullLabel || label}</span>
                  </div>
                ))}
              </div>
            </div>

            {view === VIEW_TILES ? (
              <PumpTiles
                unbillableLabel={translation?.fleetUnitNotBillableLabel}
                pumps={pumps}
                onSelectPump={(unit) => onSelectUnit(unit?.unitUId || null, unit?.licenseId)}
                waitingLabel={translation?.fleetWaitingForDataLabel || 'Waiting for data…'}
              />
            ) : null}

            {view === VIEW_GRID ? (
              <PumpStatusGrid
                unbillableLabel={translation?.fleetUnitNotBillableLabel}
                pumps={pumps}
                onSelectPump={(unit) => onSelectUnit(unit?.unitUId || null, unit?.licenseId)}
                waitingLabel={translation?.fleetWaitingForDataLabel || 'Waiting for data…'}
              />
            ) : null}

            {view === VIEW_MANIFOLD ? (
              <PumpManifold
                pumps={pumps}
                treating={treating}
                rate={downholeFlow}
                onSelectPump={(unit) => onSelectUnit(unit?.unitUId || null, unit?.licenseId)}
                waitingLabel={translation?.fleetWaitingForDataLabel || 'Waiting for data…'}
                translation={translation}
              />
            ) : null}

            {view === VIEW_READINGS ? (
              <PumpTable
                unbillableLabel={translation?.fleetUnitNotBillableLabel}
                pumps={pumps}
                layout={readingsLayout}
                selectedFieldName={chartRequest?.standardChannelName || null}
                onSelectReading={(fieldName) => onOpenFleetChart?.(fieldName)}
                waitingLabel={translation?.fleetWaitingForDataLabel || 'Waiting for data…'}
              />
            ) : null}
          </div>
        </div>
      )}

      {view === VIEW_READINGS && chartRequest && !hasNoMpcPumps ? (
        <FleetChartPanel
          fleetType={fleetType}
          licenseIds={licenseIds}
          standardChannelName={chartRequest.standardChannelName}
          dateType={chartRequest.dateType}
          dateOriginMs={chartDateOriginMs}
          channelOptions={channelOptions}
          seriesByUnit={chartSeriesByUnit}
          channelUnit={chartChannelUnit}
          fieldName={chartFieldName || chartRequest.standardChannelName}
          onChannelChange={onChartChannelChange}
          onDateTypeChange={onChartDateTypeChange}
          onClose={onCloseFleetChart}
          translation={translation}
        />
      ) : null}

      {view !== VIEW_READINGS && selectedUnitUId && !hasNoMpcPumps ? (
        <PumpDetailPanel
          unit={selectedPump || { unitUId: selectedUnitUId }}
          isLoading={isDetailLoading}
          onClose={onCloseDetail}
        />
      ) : null}
    </>
  )
}

export default FracFleetView
